/* eslint-disable import/no-duplicates */
'use client'

// copied from https://github.com/MercuryTechnologies/mercury-web/blob/0d08a24d019530db90c1c95dcf44db5ca9904387/src/components/Typography/index.tsx
import {faArrowDownToLine, faFileZipper} from '@fortawesome/pro-regular-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import * as ContextMenu from '@radix-ui/react-context-menu'
import classnames from 'classnames'
import _ from 'lodash'
import Image from 'next/image'
import React, {useMemo} from 'react'
import {unreachableCase} from '~/design-system/utils'
import {avenirNext} from '~/fonts/AvenirNextLTPro'
import {MWDSTextDiv} from '~/mercuryWebCompat/design-system/MWDSText'
import {externalLinks} from '~/routing/external-links'
import {internalLinks} from '~/routing/internal-links'
import {download} from '~/utils/fileDownloads'
import {
  useSanityReferrerData,
  type ReferrerSanityData,
} from '~/utils/useSanityReferrerData'
import {imageUrlFor} from '~/utils/useSanityReferrerData/sanity'
import {asFrontendKey, fromFrontendKey} from '~/utils/useSanityReferrerData/utils'
import {NoStyleLink} from '../NoStyleLink'
import MercuryCurrentColorSvg from './_icons/mercury-currentColor.svg'
import MercuryWhiteGradientSvg from './_icons/mercury-white-gradient.svg'
import MercuryWhiteGradient from './_icons/mercury-white-gradient.svg?url'
import TechStarsLogo from './_icons/tech-stars.svg'
import styles from './styles.module.css'

const LOGO_DOWNLOAD_URL_PREFIX = '/icons/LogoDownloads'

type LogoAndBrandProps = {
  options: LogoAndBrandOptions
  className?: string
}

type LogoAndBrandKind =
  | 'white'
  | 'specific-referrer'
  | 'colored'
  | 'black'
  | 'inherit'

type LogoAndBrandOptionsBase = {
  kind: LogoAndBrandKind
}

type LogoAndBrandOptionWhite = {
  kind: 'white'
  clickable: 'home' | false
} & LogoAndBrandOptionsBase

export type ShowReferralPartnerLogos = 'homePage' | 'ecomPage'
type LogoAndBrandOptionSpecificReferrer = {
  kind: 'specific-referrer'
  color: 'black' | 'inherit' // TODO(CP-215): I removed white since we don't need it yet
  clickable: 'home' | false
  showReferralPartnerLogos?: ShowReferralPartnerLogos
} & LogoAndBrandOptionsBase

type LogoAndBrandOptionColored = {
  kind: 'colored'
  clickable: 'home' | false
} & LogoAndBrandOptionsBase

type LogoAndBrandOptionBlack = {
  kind: 'black'
  clickable: 'home' | false
} & LogoAndBrandOptionsBase

type LogoAndBrandOptionInherit = {
  kind: 'inherit'
  clickable: 'home' | false
} & LogoAndBrandOptionsBase

export type LogoAndBrandOptions =
  | LogoAndBrandOptionWhite
  | LogoAndBrandOptionSpecificReferrer
  | LogoAndBrandOptionColored
  | LogoAndBrandOptionBlack
  | LogoAndBrandOptionInherit

export const LogoAndBrand = ({options, className}: LogoAndBrandProps) => {
  const data = useSanityReferrerData()
  const clickable = options.clickable || undefined

  const colorClassName = ((): string => {
    switch (options.kind) {
      case 'specific-referrer':
        return options.color
      case 'inherit':
        return 'inherit'
      case 'white':
        return 'white'
      case 'black':
        return 'black'
      case 'colored':
        return options.kind
      default:
        return unreachableCase(options)
    }
  })()

  const kindClassNames = ((): string[] => {
    switch (options.kind) {
      case 'specific-referrer':
        if (!data || data.referrerData.type !== 'generatedPartner') return []
        return _.compact([
          fromFrontendKey(data.referrerData.frontendKey),
          `${data?.referrerData.frontendKey}-${options.color}`,
        ]) // e.g. angellist angellist-white
      default:
        return [options.kind]
    }
  })()

  const wrapperDivClassNames = classnames(
    styles['logo-brand-wrapper'],
    kindClassNames.map(cn => styles[cn]),
    styles[colorClassName],
    !!clickable && styles.clickable
    // options.easeColor && styles.easeColor
  )

  const logo = ((): React.ReactNode => {
    let kind = options.kind
    if (options.kind === 'specific-referrer' && !data) {
      kind = 'black'
    }

    switch (kind) {
      case 'white':
        return (
          <>
            <Image alt="Mercury logo" src={MercuryWhiteGradient} />
            <MercuryBrandName />
          </>
        )
      case 'colored':
      case 'black':
        return (
          <>
            <MercuryLogoBlack />
            <MercuryBrandName />
          </>
        )
      case 'inherit':
        return (
          <>
            <MercuryCurrentColorSvg />
            <MercuryBrandName />
          </>
        )
      case 'specific-referrer': {
        return (
          <SpecificReferrerLogo
            {...(options as LogoAndBrandOptionSpecificReferrer)}
          />
        )
      }
      default:
        return unreachableCase(kind)
    }
  })()

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger className={className}>
        {clickable ? (
          <NoStyleLink href={internalLinks.home}>
            <div className={wrapperDivClassNames}>{logo}</div>
          </NoStyleLink>
        ) : (
          <div className={wrapperDivClassNames}>{logo}</div>
        )}
      </ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content
          className={classnames(styles.contextMenuContent, avenirNext.className)}
        >
          <ContextMenu.Item
            className={styles.contextMenuItem}
            onSelect={() => {
              download(
                'mercury-logo-icon.svg',
                `${LOGO_DOWNLOAD_URL_PREFIX}/mercury-logo-icon.svg`,
                'homepageIconDownload'
              )
            }}
          >
            <FontAwesomeIcon
              icon={faArrowDownToLine}
              className={styles.contextMenuIcon}
            />{' '}
            Download icon as .svg
          </ContextMenu.Item>
          <ContextMenu.Item
            className={styles.contextMenuItem}
            onSelect={() => {
              download(
                'mercury-logo-icon-and-wordmark.svg',
                `${LOGO_DOWNLOAD_URL_PREFIX}/mercury-logo-icon-and-wordmark.svg`,
                'homepageFullLogoDownload'
              )
            }}
          >
            <FontAwesomeIcon
              icon={faArrowDownToLine}
              className={styles.contextMenuIcon}
            />{' '}
            Download full logo as .svg
          </ContextMenu.Item>
          <ContextMenu.Separator className={styles.contextMenuSeparator} />
          <ContextMenu.Item
            className={styles.contextMenuItem}
            onSelect={() => {
              download(
                'mercury-logos.zip',
                externalLinks.logoDownloads.mercuryBrandKitZip,
                'homepageLogoZipDownload'
              )
            }}
          >
            <FontAwesomeIcon
              icon={faFileZipper}
              className={styles.contextMenuIcon}
            />{' '}
            Download brand kit as .zip
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  )
}

const SpecificReferrerLogo = (options: LogoAndBrandOptionSpecificReferrer) => {
  const data = useSanityReferrerData()
  const isBlack = options.color === 'black'

  const partnerLogo = useMemo(() => {
    if (!data) return null
    const {partner, referrerData} = data

    // Custom handling for tech stars
    const isTechStars =
      referrerData.type === 'generatedPartner' &&
      (referrerData.callsign === asFrontendKey('techstars-alumni-deal') ||
        referrerData.callsign === asFrontendKey('techstars-deal') ||
        referrerData.callsign === asFrontendKey('techstars-cash'))
    if (isTechStars) {
      return {
        type: 'react' as const,
        children: (
          <TechStarsLogo
            style={{
              height: '15px',
            }}
          />
        ),
      }
    }

    if (!partner) return null

    // Sometimes we don't want to show certain logos (passed down from props)
    const {showReferralPartnerLogos} = options
    const showReferralPartnerLogosMap = {
      homePage: data => data?.referrerData?.type === 'generatedPartner',
      ecomPage: data =>
        data?.referrerData?.type === 'generatedPartner' &&
        data.referrerData.audience === 'ecommerce',
    } satisfies Record<
      ShowReferralPartnerLogos,
      (props: ReferrerSanityData) => boolean
    >
    if (
      showReferralPartnerLogos &&
      !showReferralPartnerLogosMap[showReferralPartnerLogos](data)
    ) {
      return null
    }

    const frontendKey = asFrontendKey(referrerData.frontendKey)
    if (frontendKey === asFrontendKey('brex')) return null

    const referralPartnerLogo = (() => {
      switch (options.color) {
        // TODO(CP-215): We don't need white yet
        // case 'white':
        //   return (
        //     referralPartnerInfo?.logos?.find(item => item.logoType === 'light') ||
        //     referralPartnerInfo?.logos?.find(item => item.logoType === 'colored')
        //   )
        case 'black':
        case 'inherit':
          return (
            partner?.logos?.find(item => item.logoType === 'dark') ||
            partner?.logos?.find(item => item.logoType === 'colored')
          )
        default:
          return unreachableCase(options.color)
      }
    })()?.logo?.asset?._ref

    const partnerElementsClassName = classnames(
      styles[`shrink-partnerLogo`],
      styles[`hide-partnerLogo`],
      styles[`hide-${referrerData.frontendKey}`]
    )

    return {
      type: 'sanity' as const,
      referralPartnerLogo,
      partnerElementsClassName,
      referrerData: data.referrerData,
    }
  }, [data, options])

  const WrapperComponent = partnerLogo ? 'div' : React.Fragment
  const wrapperComponentProps = partnerLogo
    ? {className: styles['mercury-and-specific-logo']}
    : {}

  const partnerImage = useMemo(() => {
    if (partnerLogo?.type !== 'sanity') return null
    try {
      return {
        src: imageUrlFor(partnerLogo.referralPartnerLogo).maxHeight(40).url(),
        alt: `${partnerLogo.referrerData.name} logo`,
      }
    } catch (e) {
      return null
    }
  }, [
    partnerLogo?.referralPartnerLogo,
    partnerLogo?.referrerData?.name,
    partnerLogo?.type,
  ])
  return (
    <WrapperComponent {...wrapperComponentProps}>
      {isBlack ? <MercuryCurrentColorSvg /> : <MercuryWhiteGradientSvg />}
      <MercuryBrandName />
      {partnerLogo && partnerImage && (
        <>
          <MWDSTextDiv
            size="body-16"
            color={options.color}
            className={classnames(styles.plus, partnerLogo.partnerElementsClassName)}
          >
            +
          </MWDSTextDiv>

          {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
          <img {...partnerImage} className={partnerLogo.partnerElementsClassName} />
          {partnerLogo.type === 'react' && partnerLogo.children}
        </>
      )}
    </WrapperComponent>
  )
}

// TODO(CP-215): this is kinda weird alongside MercuryCurrentColor and MercuryWhiteGradient, but
//  this one has an `alt` tag. We should add accessibility to the SVGs and this would not be necessary, I think
export const MercuryLogoBlack = () => <MercuryCurrentColorSvg />

const MercuryBrandName = () => (
  <span className={styles.mercuryBrandName}>MERCURY</span>
)
